'use strict';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

/*** @ignore */
var Stack = /** @class */ (function () {
    function Stack(config) {
        this.stack = config.items;
        this.userFunction = config.func;
        this.delay = config.delay;
    }
    Stack.prototype.pause = function () {
        clearInterval(this._interval);
    };
    Stack.prototype.start = function () {
        if (this.isDone())
            return;
        this._interval = window.setInterval(this.fire.bind(this), this.delay);
    };
    Stack.prototype.reset = function () {
        this.stack = [];
        this.pause();
    };
    Stack.prototype.fire = function () {
        this.check();
        this.userFunction(this.stack.shift());
        this.check();
    };
    Stack.prototype.check = function () {
        if (!this.stack.length) {
            return clearInterval(this._interval);
        }
    };
    Stack.prototype.isDone = function () {
        return this.stack.length <= 0;
    };
    return Stack;
}());

/*** @ignore */
var Animation = /** @class */ (function () {
    function Animation(query) {
        this.query = query;
        this.toAnimate = Array.from(document.querySelectorAll(query));
    }
    Animation.prototype.animateItems = function (delay, func) {
        var _this = this;
        var items = this.getItemsToAnimate();
        if (!items)
            return;
        var animate = new Stack({
            items: items,
            delay: delay,
            func: function (elem) { return func ? func(elem) : _this.assignDelayAndOpacity(elem); }
        });
        animate.start();
    };
    Animation.prototype.getItemsToAnimate = function () {
        if (this.isDone())
            return;
        var _a = this.getVisibleAndHiddenItems(), visible = _a.visible, hidden = _a.hidden;
        this.toAnimate = hidden;
        return visible;
    };
    Animation.prototype.reset = function () {
        this.toAnimate = Array.from(document.querySelectorAll(this.query));
        var hidden = this.getVisibleAndHiddenItems().hidden;
        hidden.forEach(function (elem) {
            elem.style.opacity = '0';
            return elem;
        });
    };
    Animation.prototype.isDone = function () {
        return this.toAnimate.length <= 0;
    };
    Animation.prototype.assignDelayAndOpacity = function (elem) {
        if (!elem)
            return;
        var transitionDelay = this.getDelay(elem);
        Object.assign(elem.style, { opacity: 1 });
        if (transitionDelay) {
            Object.assign(elem.style, { transitionDelay: transitionDelay });
        }
    };
    Animation.prototype.getVisibleAndHiddenItems = function () {
        var _this = this;
        var model = {
            visible: [],
            hidden: []
        };
        return this.toAnimate.reduce(function (acc, item) {
            var key = _this.shouldBeVisible(item) ? 'visible' : 'hidden';
            acc[key] = acc[key].concat(item);
            return acc;
        }, model);
    };
    Animation.prototype.getDelay = function (elem) {
        if (elem && elem.dataset && elem.dataset.delay) {
            return elem.dataset.delay;
        }
    };
    Animation.prototype.shouldBeVisible = function (elem) {
        var rect = elem.getBoundingClientRect();
        var viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
        return (!(rect.bottom < 0 || rect.top - viewHeight >= 0) &&
            elem.style.opacity !== '1');
    };
    return Animation;
}());

var initalState = {
    delay: 200,
    selector: '.fadin',
    noInitalScrollEvent: false,
    animationFunction: undefined
};
var Fadin = /** @class */ (function () {
    /**
     * ```
     * import { Fader } from 'fadin';
     *
     * new Fader({selector: '.className', delay: 400})
     * ```
     * will create a new instance of fadin setup event listeners
     * and self destructs when all animations compilete
     */
    function Fadin(options) {
        if (options === void 0) { options = initalState; }
        var _this = this;
        var config = Object.assign({}, initalState, options);
        this.animationService = new Animation(config.selector);
        this._detach = this.setUpEventListener("scroll", function () {
            _this.animationService.animateItems(config.delay, config.animationFunction);
        });
        if (!config.noInitalScrollEvent)
            this.sendScroll();
    }
    /**
     * returns true when no more items are left to animate
     */
    Fadin.prototype.isDone = function () {
        return this.animationService.isDone();
    };
    /**
     * explictily removes event listeners
     */
    Fadin.prototype.detach = function () {
        return this._detach();
    };
    /**
     * hides items out view and adds items back to the stack
     */
    Fadin.prototype.reset = function () {
        this.animationService.reset();
        this.sendScroll();
    };
    /**
     * Sends scroll event to window to fade in items that are inview onload
     * fadin.sendScroll()
     *
     * just alias for `window.dispatchEvent(new Event("scroll"));`
     */
    Fadin.prototype.sendScroll = function () {
        window.dispatchEvent(new Event("scroll"));
    };
    Fadin.prototype.setUpEventListener = function (type, func) {
        var _this = this;
        var event = function () {
            if (_this.isDone()) {
                window.removeEventListener(type, event, true);
            }
            func();
        };
        window.addEventListener(type, event.bind(this));
        return function () { return window.removeEventListener(type, event, true); };
    };
    return Fadin;
}());

/**
 * You should wrap your fadin call in a on dom loaded event
 * this ensures your selected items are avalidable to be attached
 *
 * ```
 * import fadin from 'fadin'
 *
 * document.addEventListener('DOMContentLoaded', () => {
 *   fadin('.my-class', { delay: 200 })
 * })
 * ```
 */
var fadin = function (selector, options) {
    return new Fadin(__assign({ selector: selector }, options));
};

module.exports = fadin;
